import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import { getMyInfoAPI } from "../../../services/portal/student/studentService";
import StudentDetailsBox from "../../../components/portal/StudentDetailsBox";
import { submitCourseBankSlipAPI, getCourseByIdAPI } from "../../../services/portal/student/studentCourseService";
import { createCheckoutSessionAPI } from "../../../services/portal/paymentService";
import { convertFileToBase64 } from "../../../utility/Common";
import { RoutePaths } from "../../../utility/RoutePaths";

const CoursePaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const courseId = searchParams.get("courseId");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [courseDetails, setCourseDetails] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [paymentType, setPaymentType] = useState(0);
  const [formData, setFormData] = useState({
    paymentMethod: "",
    receiptNumber: "",
    bankSlip: null,
  });

  const loadCourseDetails = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await getCourseByIdAPI(courseId);
      if (res && res.IsSuccessful && res.Data) {
        setCourseDetails(res.Data);
      } else {
        showErrorToastMsg("Course not found");
      }
    } catch (error) {
      console.error("Error loading course details:", error);
      showErrorToastMsg("Failed to load course details");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const loadStudentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyInfoAPI();
      if (res && res.IsSuccessful && res.Data) {
        setStudentDetails(res.Data);
      }
    } catch (error) {
      console.error("Error loading student details:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (courseId) {
      loadCourseDetails();
    }
    if (user) {
      loadStudentDetails();
    }
  }, [courseId, user, loadCourseDetails, loadStudentDetails]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData({ ...formData, [name]: files[0] || null });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePaymentMethodChange = (e) => {
    const paymentMethod = e.target.value;
    if (paymentMethod === "Online") {
      setPaymentType(1);
    } else if (paymentMethod === "Bank Transfer") {
      setPaymentType(2);
    } else if (paymentMethod === "Free Card") {
      setPaymentType(3);
    } else {
      setPaymentType(0);
    }
    setFormData({ ...formData, paymentMethod, bankSlip: null, receiptNumber: "" });
  };

  const loadCheckoutScript = (scriptUrl) => {
    return new Promise((resolve, reject) => {
      window.errorCallback = function (error) {
        console.error("Checkout error (raw):", error);
        console.error("Checkout error (JSON):", JSON.stringify(error, null, 2));
        showErrorToastMsg("Payment error occurred. Please try again.");
        setSubmitting(false);
      };

      window.cancelCallback = function () {
        setSubmitting(false);
      };

      const existing = document.getElementById("mpgs-checkout-script");
      if (existing) {
        existing.remove();
      }

      const script = document.createElement("script");
      script.id = "mpgs-checkout-script";
      script.src = scriptUrl;
      script.setAttribute("data-error", "errorCallback");
      script.setAttribute("data-cancel", "cancelCallback");
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load checkout script"));
      document.body.appendChild(script);
    });
  };

  const handleSubmitPayment = async () => {
    if (!formData.paymentMethod) {
      showErrorToastMsg("Please select a payment method");
      return;
    }

    if (formData.paymentMethod !== "Online" && !formData.receiptNumber) {
      showErrorToastMsg("Please add receipt number");
      return;
    }

    if (formData.paymentMethod === "Bank Transfer" && !formData.bankSlip) {
      showErrorToastMsg("Please attach bank slip");
      return;
    }

    setSubmitting(true);

    try {
      if (formData.paymentMethod === "Online") {
        const params = {
          courseId: Number(courseId),
          amount: courseDetails.Fee,
          description: `Payment for ${courseDetails.Title}`,
        };
        const res = await createCheckoutSessionAPI(params);

        if (res && res.result === "SUCCESS" && res.session) {
          const successIndicator = res.successIndicator;
          const sessionId = res.session.id;
          sessionStorage.setItem("paymentSuccessIndicator", successIndicator);

          await loadCheckoutScript(process.env.REACT_APP_PAYMENT_CHECKOUT_URL);

          window.Checkout.configure({ session: { id: sessionId } });
          window.Checkout.showPaymentPage();
        } else {
          showErrorToastMsg("Failed to submit payment");
        }
      } else if (formData.paymentMethod === "Bank Transfer") {
        const bankSlipBase64 = await convertFileToBase64(formData.bankSlip);
        const params = {
          courseId: Number(courseId),
          amount: courseDetails.Fee,
          number: formData.receiptNumber,
          image: bankSlipBase64,
          isFreePass: false,
        };
        const uploadRes = await submitCourseBankSlipAPI(params);
        if (uploadRes && uploadRes.IsSuccessful) {
          showSuccessToastMsg(uploadRes.Message || "Payment submitted successfully");
          navigate(`${RoutePaths.STUDENT_COURSE_DETAILS_PAGE}?courseId=${courseId}`);
        } else {
          showErrorToastMsg(uploadRes?.Message || "Failed to submit payment");
        }
      } else if (formData.paymentMethod === "Free Card") {
        const params = {
          courseId: courseId,
          amount: 0,
          number: formData.receiptNumber,
          image: "",
          isFreePass: true,
        };
        const uploadRes = await submitCourseBankSlipAPI(params);
        if (uploadRes && uploadRes.IsSuccessful) {
          showSuccessToastMsg(uploadRes.Message || "Request submitted successfully");
          navigate(`${RoutePaths.STUDENT_COURSE_DETAILS_PAGE}?courseId=${courseId}`);
        } else {
          showErrorToastMsg(uploadRes?.Message || "Failed to submit payment");
        }
      } else {
        showErrorToastMsg("Unsupported payment method");
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      showErrorToastMsg("Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!courseId) {
    return (
      <DashboardLayout title="Payment" subtitle="Course ID is required">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Course ID is required</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Payment"
      subtitle={`Payment for ${courseDetails?.Title || ""}`}
    >
      <div>
        <div className="mb-4">
          <button
            onClick={() =>
              navigate(`${RoutePaths.STUDENT_COURSE_DETAILS_PAGE}?courseId=${courseId}`)
            }
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            ← Back to Course
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : (
          <>
            <StudentDetailsBox studentDetails={studentDetails} />

            {/* Course Details Box */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-primary-600">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {courseDetails?.Title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courseDetails?.Date && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Date:</span>
                    <p className="font-semibold text-sm text-gray-900">
                      {new Date(courseDetails.Date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                )}
                {courseDetails?.Fee !== undefined && courseDetails?.Fee !== null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Fee:</span>
                    <p className="font-semibold text-sm text-gray-900">
                      LKR {courseDetails.Fee.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Form */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                Payment Information
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmitPayment();
                }}
              >
                <div className="space-y-6">
                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handlePaymentMethodChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select Payment Method</option>
                      <option value="Online">Online</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Free Card">Free Card</option>
                    </select>
                  </div>

                  {/* Online Payment Info */}
                  {paymentType === 1 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-2">
                        Online Payment
                      </h4>
                      <p className="text-sm text-gray-600">
                        You will be redirected to the Bank of Ceylon secure payment
                        page to complete your payment of{" "}
                        <span className="font-semibold">
                          LKR {courseDetails?.Fee?.toFixed(2)}
                        </span>
                        .
                      </p>
                      <div className="d-flex justify-center items-center">
                        <p className="text-md text-gray-500 mt-8 font-semibold">
                          We Accept Card Payments
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <img
                            src="/images/visacard.jpg"
                            alt="Visa"
                            className="h-8 object-contain"
                          />
                          <img
                            src="/images/mastercard.svg"
                            alt="MasterCard"
                            className="h-10 object-contain"
                            style={{ marginLeft: "40px" }}
                          />
                          <img
                            src="/images/chinapay.png"
                            alt="ChinaPay"
                            className="h-20 object-contain"
                          />
                          <img
                            src="/images/bocipgblack.jpg"
                            alt="BOC IPG"
                            className="h-10 object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Account Details */}
                  {paymentType === 2 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Bank Account Details
                      </h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number: 95760014
                        </label>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Royal econ (pvt)
                        </label>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank of Ceylon
                        </label>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maharagama
                        </label>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kottawa
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Form Fields */}
                  {paymentType === 2 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Bank Transfer Details
                      </h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Receipt Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="receiptNumber"
                          onChange={handleChange}
                          className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Attach Bank Slip <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="file"
                          name="bankSlip"
                          onChange={handleChange}
                          accept="image/*"
                          className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                        {formData.bankSlip && (
                          <p className="text-xs text-gray-600 mt-1">
                            Selected: {formData.bankSlip.name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Accepted formats: Image files (JPG, PNG)
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Free Card Form Fields */}
                  {paymentType === 3 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Free Card Details
                      </h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Free Card Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="receiptNumber"
                          onChange={handleChange}
                          className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`${RoutePaths.STUDENT_COURSE_DETAILS_PAGE}?courseId=${courseId}`)
                      }
                      className="text-sm font-medium px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    {(paymentType === 1 || paymentType === 2 || paymentType === 3) && (
                      <button
                        type="submit"
                        disabled={submitting}
                        className={`text-sm font-medium px-6 py-2 rounded-md transition-colors ${
                          submitting
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-primary-600 text-white hover:bg-primary-700"
                        }`}
                      >
                        {submitting
                          ? paymentType === 1
                            ? "Redirecting..."
                            : "Submitting..."
                          : paymentType === 1
                            ? "Pay Now"
                            : "Submit"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CoursePaymentPage;
